import { createModal } from './modal.js';
import { save } from './persist.js';

export function initOccasionName() {
  document.querySelectorAll('[data-occasion-name]').forEach(wrap => {
    const display      = wrap.querySelector('.occ-display');
    const modal        = wrap.querySelector('.occ-modal');
    const input        = modal.querySelector('.occ-input');
    const confirmBtn   = modal.querySelector('.modal__confirm');
    const errorEl      = modal.querySelector('.modal__error');
    const fieldWrapper = input.closest('.form-field');
    const fieldMsgEl   = fieldWrapper?.querySelector('.form-field__message');

    const confirmDefault = confirmBtn.textContent;

    let activeOpener = null;
    let isSaving     = false;

    function clearError() {
      if (!errorEl) return;
      errorEl.textContent = '';
      errorEl.hidden = true;
    }

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.hidden = false;
    }

    function setFieldError(msg) {
      if (!fieldWrapper) return;
      if (msg) {
        fieldWrapper.classList.add('has-error');
        input.setAttribute('aria-invalid', 'true');
      } else {
        fieldWrapper.classList.remove('has-error');
        input.setAttribute('aria-invalid', 'false');
      }
      if (fieldMsgEl) fieldMsgEl.textContent = msg || '';
    }

    function clearAllErrors() {
      clearError();
      setFieldError('');
    }

    // Apply server-returned errors in the {errors: [{field?, message}]}
    // shape (see persist.js). Items keyed to the `name` field decorate
    // the input; anything else (or items without a field key) falls
    // into the generic modal__error block. With no errors at all,
    // surface a fallback so the customer still gets a signal.
    function applyServerErrors(errors) {
      const list = Array.isArray(errors) ? errors : [];
      if (list.length === 0) {
        showError('Could not save occasion name. Please try again.');
        return;
      }
      const globals = [];
      let firstFieldError = false;
      list.forEach(({ field, message }) => {
        if (field === 'name') {
          setFieldError(message);
          firstFieldError = true;
        } else if (message) {
          globals.push(message);
        }
      });
      if (globals.length) showError(globals.join(' '));
      if (firstFieldError) input.focus();
    }

    function updateConfirmState() {
      confirmBtn.disabled = isSaving || input.value.trim().length === 0;
    }

    function setSaving(saving) {
      isSaving = saving;
      modal.classList.toggle('is-saving', saving);
      confirmBtn.textContent = saving ? 'Saving…' : confirmDefault;
      updateConfirmState();
    }

    function primeForOpener(opener) {
      activeOpener = opener || null;
      clearAllErrors();
      setSaving(false);
      input.value = opener?.dataset.savedId || '';
      updateConfirmState();
      requestAnimationFrame(() => input.focus());
    }

    const inline = wrap.hasAttribute('data-occasion-name-inline');

    let close = () => {};
    if (inline) {
      updateConfirmState();
    } else {
      ({ close } = createModal(modal, {
        closeOnBackdrop: false,
        onBeforeOpen: primeForOpener,
        onClose: () => {
          activeOpener = null;
          clearAllErrors();
          setSaving(false);
        },
      }));
    }

    // Clear errors as soon as the customer starts correcting the input,
    // so the .has-error styling doesn't linger past the edit.
    input.addEventListener('input', () => {
      setFieldError('');
      clearError();
      updateConfirmState();
    });

    confirmBtn.addEventListener('click', async () => {
      const value = input.value.trim();
      if (!value || isSaving) return;
      const saveUrl = activeOpener?.dataset.saveUrl;

      if (saveUrl) {
        clearAllErrors();
        setSaving(true);
        try {
          await save({ saveUrl, payload: { name: value } });
        } catch (err) {
          console.error(err);
          applyServerErrors(err?.errors);
          setSaving(false);
          return;
        }
        setSaving(false);
      }

      if (activeOpener) {
        activeOpener.dataset.savedId = value;
        activeOpener.dispatchEvent(new CustomEvent('occasion-name:confirm', {
          bubbles: true,
          detail: { value },
        }));
      }
      if (display) display.textContent = value;
      close();
    });
  });
}
